import { gql, useQuery } from '@apollo/client'
import { isEmpty } from '@navinc/utils'
import { fragments } from '../../graphql/fragments'
import { getIsSessionExpired } from '../../utils/is-session-expired.js'

export const ACCOUNT_QUERY = gql`
  query UseAccount {
    account {
      hasCompletedFinancingProfile @client
      hasCompletedSomeFinancingProfile @client
      isVdfIntentComplete @client
      id
      email
      personalCreditCheckOptInAt
      ownerData {
        ...FullPerson
        address {
          ...FullAddress
        }
      }
      businesses {
        id
        isPreferred
        annualRevenueCents
        businessNaicsCode
        businessNaicsCodeDescription
        businessStartDate
        businessHasBankruptcies
        city
        createdAt
        equifaxID
        experianBIN
        fundingAmountCents
        fundingPurpose
        fundingSpeedDays
        name
        ownerCreditScore
        ownerCreditScoreSource
        ownerHouseholdIncomeCents
        phone
        startDate
        state
        updatedAt
        zip
        interestedInFinancing
        ownerBestContactPhoneNumber
        businessAddress {
          ...FullAddress
        }
      }
    }
  }
  ${fragments.FullPerson}
  ${fragments.FullAddress}
`
export const useAccount = () => {
  const isSessionExpired = getIsSessionExpired()
  const resp = useQuery(ACCOUNT_QUERY, {
    skip: isSessionExpired,
  })
  const { data: { account } = {} } = resp
  const {
    email,
    id,
    ownerData = {},
    hasCompletedFinancingProfile,
    hasCompletedSomeFinancingProfile,
    isVdfIntentComplete,
    businesses = [],
    personalCreditCheckOptInAt,
  } = account ?? {}
  const firstBusiness = businesses[0]
  const { experianBIN, equifaxID } = firstBusiness ?? {}

  return {
    ...resp, // Provide all the other Apollo things
    account,
    getOwnerData: () => ownerData,
    getAccountId: () => id ?? '',
    getIsOptedIn: () => !!personalCreditCheckOptInAt,
    getFirstBusiness: () => firstBusiness ?? {},
    getBusinessBin: () => experianBIN,
    getBusinessEqxId: () => equifaxID,
    getIsTrackingDataReady: () => !isEmpty(email) && !isEmpty(ownerData) && !isEmpty(firstBusiness),
    getHasCompletedKeyDataVdfIntentFlow: () => {
      const {
        ownerBestContactPhoneNumber,
        startDate,
        fundingAmountCents,
        fundingSpeedDays,
        fundingPurpose,
        interestedInFinancing,
      } = firstBusiness || {}
      return (
        !!ownerBestContactPhoneNumber &&
        !!startDate &&
        !!fundingAmountCents &&
        !!fundingSpeedDays &&
        !!fundingPurpose &&
        !!interestedInFinancing === true
      )
    },
    hasCompletedFinancingProfile,
    hasCompletedSomeFinancingProfile,
    isVdfIntentComplete,
  }
}

/**
 * This is useful for class components that would like to benefit from the hook.
 */
export const withAccount = (Component) => (props) => {
  const resp = useAccount()
  return <Component {...props} useAccount={resp} />
}
